/**
 * MessageSearch Component
 *
 * Full-text search for messages with filters and highlighted results.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Simple debounce utility
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

export interface SearchResult {
  message: {
    id: string;
    content: string;
    channel_id: string;
    project_id: string;
    sender_type: string;
    sender_user_id?: string;
    sender_agent_id?: string;
    message_type: string;
    created_at: string;
    has_attachments: boolean;
  };
  rank: number;
  headline: string;
}

export interface SearchFilters {
  channel_id?: string;
  sender_type?: string;
  sender_user_id?: string;
  sender_agent_id?: string;
  message_type?: string;
  date_from?: string;
  date_to?: string;
  has_attachments?: boolean;
}

interface MessageSearchProps {
  projectId: string;
  onResultClick?: (messageId: string, channelId: string) => void;
  className?: string;
}

export const MessageSearch: React.FC<MessageSearchProps> = ({
  projectId,
  onResultClick,
  className = '',
}) => {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced search function
  const performSearch = useCallback(
    debounce(async (searchQuery: string, searchFilters: SearchFilters) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setTotalCount(0);
        setHasSearched(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const response = await api.post(`/inbox/projects/${projectId}/search`, {
          query: searchQuery,
          ...searchFilters,
        });
        setResults(response.data.results);
        setTotalCount(response.data.total_count);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Search failed');
        setResults([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [projectId]
  );

  // Trigger search on query or filter change
  useEffect(() => {
    performSearch(query, filters);
  }, [query, filters, performSearch]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearSearch = () => {
    setQuery('');
    setFilters({});
    setResults([]);
    setTotalCount(0);
    setHasSearched(false);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
            />
            {query && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 border rounded-lg transition-colors
              ${showFilters
                ? 'border-blue-500 bg-blue-500/10 dark:bg-blue-900/30 text-blue-600'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            title="Search filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-4">
            {/* Sender Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sender Type
              </label>
              <select
                value={filters.sender_type || ''}
                onChange={(e) => setFilters({ ...filters, sender_type: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              >
                <option value="">All</option>
                <option value="user">User</option>
                <option value="agent">Agent</option>
                <option value="system">System</option>
              </select>
            </div>

            {/* Message Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message Type
              </label>
              <select
                value={filters.message_type || ''}
                onChange={(e) => setFilters({ ...filters, message_type: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              >
                <option value="">All</option>
                <option value="user_message">User Message</option>
                <option value="agent_message">Agent Message</option>
                <option value="hitl_request">HITL Request</option>
                <option value="status_update">Status Update</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Has Attachments */}
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.has_attachments === true}
                  onChange={(e) => setFilters({
                    ...filters,
                    has_attachments: e.target.checked ? true : undefined
                  })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Has attachments only
                </span>
              </label>
            </div>
          </div>

          {/* Clear Filters */}
          {Object.values(filters).some(v => v !== undefined) && (
            <button
              onClick={() => setFilters({})}
              className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="mt-4 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-500/10 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        ) : hasSearched && results.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">
              No messages found
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Try different keywords or adjust your filters
            </p>
          </div>
        ) : results.length > 0 ? (
          <>
            {/* Results count */}
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Found {totalCount} result{totalCount !== 1 ? 's' : ''}
            </div>

            {/* Results list */}
            <div className="space-y-3">
              {results.map((result) => (
                <div
                  key={result.message.id}
                  onClick={() => onResultClick?.(result.message.id, result.message.channel_id)}
                  className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700
                    hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-colors"
                >
                  {/* Message header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs
                        ${result.message.sender_type === 'user' ? 'bg-blue-500' :
                          result.message.sender_type === 'agent' ? 'bg-purple-500' : 'bg-gray-500'}`}>
                        {result.message.sender_type === 'agent' ? 'A' : 'U'}
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {result.message.sender_type}
                      </span>
                      {result.message.has_attachments && (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(result.message.created_at)}
                    </span>
                  </div>

                  {/* Highlighted content */}
                  <div
                    className="text-sm text-gray-600 dark:text-gray-300 [&_mark]:bg-yellow-200 dark:[&_mark]:bg-yellow-800 [&_mark]:px-0.5 [&_mark]:rounded"
                    dangerouslySetInnerHTML={{ __html: result.headline }}
                  />

                  {/* Relevance score */}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1 flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${Math.min(result.rank * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400">
                      {Math.round(result.rank * 100)}% match
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MessageSearch;
